import { useCallback, useState } from 'react';
import type { WebViewMessageEvent } from 'react-native-webview';
import {
  logger as LOG,
  useNoticeBannersStackContext,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

LOG.enable('ApplePay');
const logger = LOG.extend('ApplePay');

export const useApplePayMessages = (props: ApplePayMessagesHandlerProps) => {
  const { onCapturePaymentMethodId } = props;
  const handleApplePayError = useApplePayErrorHandling();
  const [isApplePayEnabled, setIsApplePayEnabled] = useState(false);

  const handleApplePayMessages = useCallback(
    async (event: WebViewMessageEvent) => {
      const message = parseApplePayMessage(event);
      const { paymentMethodId, applePayEnabled, error } = message;

      logger.info(message);

      if (error) {
        handleApplePayError(error);
      }

      if (applePayEnabled) {
        setIsApplePayEnabled(true);
      }

      if (paymentMethodId) {
        await onCapturePaymentMethodId?.({
          id: paymentMethodId,
          nickname: 'Apple Pay',
          isDefault: true,
        });
      }
    },
    [handleApplePayError, onCapturePaymentMethodId],
  );

  return { isApplePayEnabled, handleApplePayMessages };
};

const useApplePayErrorHandling = () => {
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  return useCallback(
    (error: Readonly<{ message?: string }>) => {
      logger.error(error);

      addNoticeBanner({
        text: error?.message ?? t('general.apple-pay-error'),
        palette: 'caution',
      });
    },
    [t, addNoticeBanner],
  );
};

const parseApplePayMessage = (event: WebViewMessageEvent) => {
  const dataString = event.nativeEvent.data ?? '{}';
  const parsedData = JSON.parse(dataString) as HostedFieldsPayload;

  return parsedData;
};

// ─── Types ─────────────────────────────────────────────────────────────────────

type ApplePayMessagesHandlerProps = Readonly<{
  onCapturePaymentMethodId?: (props: {
    id: string;
    nickname: string;
    isDefault: boolean;
  }) => Promise<void>;
}>;

type HostedFieldsPayload = Readonly<{
  log?: string;
  paymentMethodId?: string;
  applePayEnabled?: boolean;
  error?: { message?: string };
}>;
